import React, { useCallback, useState } from "react";
import {
  FlatList,
  Modal,
  StyleSheet,
  Text,
  TouchableOpacity,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { SafeAreaView } from "react-native-safe-area-context";

import ModalHeader from "./ModalHeader";

import { toReadableText } from "@/formatting/text";
import { type AttributeValue } from "@/schemas/attribute/AttributeSet";
import { type UserAttributeSet } from "@/schemas/profile/UserAttributeSet";

type ModalProps = {
  attribute: UserAttributeSet;
  values: AttributeValue[];
  showModal: boolean;
  closeModal: () => void;
  updateAttributes: (items: AttributeValue[]) => void;
};

function OptionSelectionModal({
  attribute,
  values,
  showModal,
  closeModal,
  updateAttributes,
}: ModalProps): React.JSX.Element {
  const [selectedOptions, setSelectedOptions] =
    useState<AttributeValue[]>(values);
  const toggleSelection = (item: AttributeValue): void => {
    if (attribute.isMultiSelect) {
      setSelectedOptions((prev) =>
        prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item],
      );
    } else {
      setSelectedOptions([item]);
    }
  };

  const onSave = useCallback(() => {
    const newValues = Object.values(attribute.map).filter((option) =>
      selectedOptions.includes(option),
    );
    updateAttributes(newValues);
    closeModal();
  }, [selectedOptions]);

  const renderOption = ({
    item,
  }: {
    item: AttributeValue;
  }): React.JSX.Element => (
    <TouchableOpacity style={styles.item} onPress={() => toggleSelection(item)}>
      <Text style={styles.itemText}>{item.label}</Text>
      {selectedOptions.includes(item) && (
        <Ionicons name="checkmark" size={24} color="#007AFF" />
      )}
    </TouchableOpacity>
  );

  return (
    <Modal
      visible={showModal}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <SafeAreaView style={styles.container} edges={["top"]}>
        <ModalHeader
          closeModal={closeModal}
          handleSave={onSave}
          title={toReadableText(attribute.id)}
          saveDisabled={selectedOptions.length === 0}
        />
        <FlatList
          data={Object.values(attribute.map)}
          renderItem={renderOption}
          keyExtractor={(item) => item.id}
        />
      </SafeAreaView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  item: {
    alignItems: "center",
    borderBottomColor: "#E0E0E0",
    borderBottomWidth: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  itemText: {
    color: "#000000",
    fontSize: 16,
  },
});

export default OptionSelectionModal;
